import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DevicesIcon from '@mui/icons-material/Devices';
import AutomationIcon from '@mui/icons-material/SmartToy';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import SensorsIcon from '@mui/icons-material/Sensors';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Section,
  PageHero,
  SectionTitle,
  AnimatedBox,
  GradientButton,
} from '../../components/ui';

const ServiceDetailCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: '#FFFFFF',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  transition: 'all 0.4s ease',
  overflow: 'visible',
  position: 'relative',

  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12)',

    '& .service-icon': {
      transform: 'scale(1.1) rotate(5deg)',
    },
  },
}));

const IconWrapper = styled(Box)(({ theme, gradient }) => ({
  width: '60px',
  height: '60px',
  flexShrink: 0,
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: gradient || theme.palette.accent.gradient,
  boxShadow: '0 8px 30px rgba(0, 184, 212, 0.3)',
  transition: 'all 0.3s ease',

  '& svg': {
    fontSize: '28px',
    color: '#FFFFFF',
  },
}));

const FeatureItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.5, 0),

  '& .MuiListItemIcon-root': {
    minWidth: '32px',
  },

  '& .MuiSvgIcon-root': {
    fontSize: '18px',
    color: theme.palette.primary.main,
  },
}));

const services = [
  {
    key: 'digitization',
    icon: <DevicesIcon />,
    gradient: 'linear-gradient(135deg, #00D4FF 0%, #00B8D4 100%)',
    features: [
      'Process Analysis & Mapping',
      'Digital Workflow Design',
      'Document Management',
      'Data Migration',
    ],
  },
  {
    key: 'automation',
    icon: <AutomationIcon />,
    gradient: 'linear-gradient(135deg, #5B8DEF 0%, #3D6FD9 100%)',
    features: [
      'Workflow Automation',
      'RPA Implementation',
      'Business Rule Engines',
      'Integration Services',
    ],
  },
  {
    key: 'development',
    icon: <CodeIcon />,
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    features: [
      'Web Applications',
      'Mobile Apps (iOS/Android)',
      'Progressive Web Apps',
      'API Development',
    ],
  },
  {
    key: 'enterprise',
    icon: <StorageIcon />,
    gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    features: [
      'Enterprise Architecture',
      'Microservices',
      'Cloud Solutions',
      'System Integration',
    ],
  },
  {
    key: 'iot',
    icon: <SensorsIcon />,
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    features: [
      'Sensor Integration',
      'Real-time Monitoring',
      'Edge Computing',
      'Predictive Maintenance',
    ],
  },
  {
    key: 'consulting',
    icon: <SupportAgentIcon />,
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    features: [
      'Technology Strategy',
      'Digital Roadmapping',
      'Vendor Selection',
      'Implementation Support',
    ],
  },
];

export default function Services() {
  const { t } = useTranslation();

  return (
    <>
      {/* Page Header */}
      <PageHero title={t('services.title')} subtitle={t('services.subtitle')} />

      {/* Services Grid */}
      <Section>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={service.key}>
                <AnimatedBox
                  animation="fadeInUp"
                  delay={`${index * 0.1}s`}
                  sx={{ height: '100%' }}
                >
                  <ServiceDetailCard>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <IconWrapper gradient={service.gradient} className="service-icon">
                          {service.icon}
                        </IconWrapper>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          {t(`services.${service.key}.title`)}
                        </Typography>
                      </Box>

                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          mb: 3,
                          lineHeight: 1.7,
                        }}
                      >
                        {t(`services.${service.key}.description`)}
                      </Typography>

                      <List disablePadding>
                        {service.features.map((feature, idx) => (
                          <FeatureItem key={idx} disableGutters>
                            <ListItemIcon>
                              <CheckCircleIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{
                                variant: 'body2',
                                fontWeight: 500,
                              }}
                            />
                          </FeatureItem>
                        ))}
                      </List>
                    </CardContent>
                  </ServiceDetailCard>
                </AnimatedBox>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* CTA Section */}
      <Section variant="light">
        <Container maxWidth="md">
          <AnimatedBox animation="fadeInUp">
            <Box
              sx={{
                textAlign: 'center',
                background: (theme) => theme.palette.accent.gradient,
                borderRadius: '24px',
                p: { xs: 4, md: 6 },
                position: 'relative',
                overflow: 'hidden',

                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-50%',
                  right: '-20%',
                  width: '400px',
                  height: '400px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 700,
                  mb: 2,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                Need a custom solution?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  mb: 4,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                Let's discuss how we can help transform your business with tailored digital solutions.
              </Typography>
              <GradientButton
                component={Link}
                to="/contact"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  background: '#FFFFFF',
                  color: 'primary.main',
                  position: 'relative',
                  zIndex: 1,
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.9)',
                  },
                }}
              >
                {t('common.contactUs')}
              </GradientButton>
            </Box>
          </AnimatedBox>
        </Container>
      </Section>
    </>
  );
}
